import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, Music, BarChart2, Link2, ListMusic, Clock, 
  CreditCard, Tag, Youtube, Film, Headphones, Trophy,
  CheckCircle, Mic2, Building2, Timer,
  Users, Megaphone, Target, Heart, Calendar,
  Send, ArrowRight
} from 'lucide-react';
import './BenefitsPage.css';

const BenefitsPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (pageRef.current) {
      const fadeElements = pageRef.current.querySelectorAll('.fade-in');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });

      fadeElements.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    }
  }, []);

  const distributionFeatures = [
    {
      icon: Music,
      title: "Unlimited Music Releases",
      description: "Release as much music as you want across the year. No limits on singles, EPs, or albums—keep your creative flow uninterrupted."
    },
    {
      icon: Users,
      title: "Automatic Royalty Splits",
      description: "Effortlessly split earnings with collaborators, producers, or band members. Our system handles the math and payouts so everyone gets paid fairly and on time."
    },
    {
      icon: BarChart2,
      title: "In-Depth Analytics",
      description: "Understand your audience. Track streaming trends, listener locations, and growth metrics to make data-informed career decisions."
    },
    {
      icon: Globe,
      title: "Global Reach (150+ Platforms)",
      description: "Instant delivery to Spotify, Apple Music, Amazon, TikTok, Instagram, YouTube Music, Tidal, Deezer, and 140+ more. Automatic delivery to new platforms as they launch."
    },
    {
      icon: Link2,
      title: "Pre-Save Smartlinks",
      description: "Build momentum before drop day. Drive pre-saves across all major platforms from a single link and track fan engagement to optimize your launch."
    },
    {
      icon: ListMusic,
      title: "Playlist Pitching Support",
      description: "Get your music in front of curators. Submit tracks for Spotify editorial consideration and access third-party pitching tools to boost your streams."
    },
    {
      icon: Clock,
      title: "Fast Royalty Payouts",
      description: "Cash flow matters. Receive direct-to-bank monthly payments without the industry-standard 90+ day waiting periods."
    },
    {
      icon: Tag,
      title: "Custom Label Name",
      description: "Maintain your brand identity. Choose to release under \"Anyone Music Records\" or establish your own custom label name on streaming platforms."
    },
    {
      icon: Youtube,
      title: "YouTube Content ID",
      description: "Monetize your music across YouTube. Protect your copyright and earn revenue whenever your tracks are used in videos, including user-generated content."
    },
    {
      icon: Film,
      title: "Sync Licensing Opportunities",
      description: "Pitch your tracks for placement in TV, film, ads, and video games. We provide access to sync briefs and opportunities to expand your revenue."
    },
    {
      icon: Headphones,
      title: "Priority Support",
      description: "Never get stuck in a queue. Get fast responses from a dedicated artist support team comprising real humans who understand the industry."
    },
    {
      icon: Trophy,
      title: "Official Chart Registration",
      description: "Make it official. Your releases are registered to count toward official music charts worldwide, giving you the credit you deserve."
    },
    {
      icon: CheckCircle,
      title: "Instant Spotify Verification",
      description: "Look professional from day one. Get a verified artist profile (blue check) on Spotify and instant access to Spotify for Artists tools."
    },
    {
      icon: CreditCard,
      title: "Music Publishing Collection",
      description: "Don't leave money on the table. Collect publishing royalties globally from radio, streaming, and live performances."
    },
    {
      icon: Timer,
      title: "Timed & Compilation Releases",
      description: "Coordinate global launches by choosing exact release times. Easily release collaborative projects and multi-artist compilations."
    }
  ];

  const amrDifference = [
    {
      icon: Users,
      title: "Community & Mentorship",
      description: "Founded by artists, for artists. Gain access to experienced mentors like Nico Aguilera, Gerry Sullivan, and George Barakat who provide real-world guidance on branding, production, and navigating the industry."
    },
    {
      icon: Megaphone,
      title: "Marketing Strategy Support",
      description: "Great music deserves to be heard. Leverage our decades of marketing and brand-building experience. We provide personalized guidance to help you build a signature sound and visual identity that cuts through the noise."
    },
    {
      icon: Target,
      title: "Artist Development",
      description: "We believe in the long game. Our support goes beyond streaming numbers; we focus on building sustainable careers, loyal fanbases, and artistic longevity rather than chasing fleeting viral moments."
    },
    {
      icon: Heart,
      title: "Inclusive Philosophy",
      description: "\"Music lives in everyone\"—we mean that. We are door-openers, not gatekeepers. We believe that being independent shouldn't mean being alone. Join a community that celebrates diversity in sound and background."
    },
    {
      icon: Calendar,
      title: "Live Events & Showcases",
      description: "Your music deserves to be experienced live. We regularly curate and produce live events and showcases. From intimate listening sessions to full-scale performances, we create opportunities for you to connect with fans and industry professionals."
    }
  ];

  const whoIsThisFor = [
    "You are an independent artist ready to take your music career seriously.",
    "You are looking for more than just a distributor—you want a support system and community.",
    "You believe in building something real, authentic, and sustainable.",
    "You value creative control and ownership of your masters.",
    "You are ready to grow and put in the work to be heard."
  ];

  return (
    <div className="benefits-page" ref={pageRef} data-testid="benefits-page">
      {/* Hero Section */}
      <section className="benefits-hero">
        <div className="benefits-hero-bg"></div>
        <div className="benefits-hero-overlay"></div>
        <div className="container benefits-hero-content">
          <span className="hero-overline fade-in">For Artists</span>
          <h1 className="fade-in">Artist Support</h1>
          <p className="hero-tagline fade-in">Everything you need to release, grow, and thrive as an independent artist.</p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="benefits-intro">
        <div className="container">
          <div className="intro-content fade-in">
            <p>
              Welcome to Anyone Music Records. We believe that the barriers to entry in the music industry shouldn't exist. 
              We are dedicated to partnering with independent and emerging artists who are ready to take control of their careers.
            </p>
            <p>
              By combining a community-first philosophy with world-class infrastructure, we offer a home for artists who want to build something sustainable.
              Through our strategic partnership with <strong>Ditto Music</strong>, we provide industry-leading distribution technology usually reserved for major labels, 
              ensuring your music reaches every corner of the globe while you retain creative control and ownership.
            </p>
          </div>
        </div>
      </section>

      {/* Distribution Features */}
      <section className="distribution-section">
        <div className="container">
          <div className="section-header fade-in">
            <h2>What We Offer: Global Distribution</h2>
            <p>Powered by our partnership with Ditto Music, we provide a comprehensive suite of tools to get your music heard.</p>
          </div>

          <div className="distribution-grid">
            {distributionFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="distribution-card fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div className="distribution-icon">
                    <IconComponent size={24} />
                  </div>
                  <div className="distribution-content">
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AMR Difference */}
      <section className="amr-difference">
        <div className="container">
          <div className="section-header fade-in">
            <h2>The Anyone Music Records Difference</h2>
            <p className="difference-tagline">Distribution is just the vehicle. Anyone Music Records is the fuel.</p>
            <p>Beyond getting your music online, we offer an ecosystem designed for growth.</p>
          </div>

          <div className="difference-grid">
            {amrDifference.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="difference-card fade-in">
                  <div className="difference-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="who-section">
        <div className="container">
          <div className="who-content fade-in">
            <h2>Who Is This For?</h2>
            <p className="who-intro">You are a great fit for Anyone Music Records if:</p>
            <ul className="who-list">
              {whoIsThisFor.map((item, index) => (
                <li key={index}>
                  <CheckCircle size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Demo Submission CTA */}
      <section className="demo-section">
        <div className="container">
          <div className="demo-cta fade-in">
            <div className="demo-cta-content">
              <Mic2 size={48} className="demo-icon" />
              <h2>Ready to Share Your Music?</h2>
              <p>We're always looking for fresh talent. Submit your demo and let us hear what you've got.</p>
              <Link to="/submit-demo" className="btn btn-primary demo-cta-btn" data-testid="submit-demo-btn">
                <Send size={18} />
                Submit Your Demo
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="benefits-cta">
        <div className="container">
          <div className="cta-content fade-in">
            <h2>Ready to Partner With Us?</h2>
            <p>Interested in partnering with Anyone Music Records? Let's start the conversation. We'd love to hear your story and your music.</p>

            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary">Get In Touch</a>
              <a href="/about" className="btn btn-secondary">Learn More About Us</a>
            </div>

            <p className="cta-closing">Music Lives In Everyone. We Just Turn It Up.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BenefitsPage;
